import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { LoginService } from '@shared-angular/services/login.service';
import { RagLoggerService } from '@shared-angular/services/rag-logger/rag-logger.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { UsertokenService } from '@shared-angular/services/usertoken.service';
import { BotLocale, ChatbotConfig, HistoryItemResponse, MessageItem, SessionState, WordReplacement } from '@shared/index';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { MarkdownService } from 'ngx-markdown';
import { Subscription } from 'rxjs';
import { DisclaimerModalComponent } from 'src/app/shared/components/ai-bot/utils/disclaimer-modal/disclaimer-modal.component';
import { SupervisorMessageModalComponent } from 'src/app/shared/components/ai-bot/utils/supervisor-message-modal/supervisor-message-modal.component';
import { BotMessagesService } from 'src/app/shared/services/bot-messages.service';
import { BOTEVENT, BotService } from 'src/app/shared/services/bot.service';
import { IframeCommunicationService } from 'src/app/shared/services/iframe-communication/iframe-communication.service';
import { OverlayManagerService } from 'src/app/shared/services/overlay-manager.service';
import { PlayAudioService } from 'src/app/shared/services/playaudio.service';
import { SpeechService } from 'src/app/shared/services/speech.service';
import { SocketService } from '../../../shared/services/socket.service';
import { StatisticService } from '../admin/tabs/insights/statistics-tab/statistic.service';
import { CardModalComponent } from './cardModal/cardModal.component';
@Component({
  template: "",
  selector: 'app-bot-based',
  imports: [FormsModule, BsDropdownModule],
  providers: [BsModalService]
})
export class BotBasedComponent implements OnInit, OnDestroy {
  public botConfig!: ChatbotConfig;
  public supervisorAnswerModalRef?: BsModalRef;
  public promptAsText: boolean = false;
  public isThinking: boolean = false;
  public isRecivingDeltas: boolean = false;
  public isSending: boolean = false;
  public prevBotState: SessionState = 'unknown';
  protected lastRunCompleteMessageId = ''; // verhindert doppelte runComplete-Calls im DialogRecorder
  public botState: SessionState = 'unknown';
  public promptInput: string = '';
  protected customCssUrl!: SafeResourceUrl;
  protected currentThread: string = '';
  protected subscriptions: Subscription[] = [];
  protected recognitionLanguage: string = 'de-DE';
  /** Steuert, ob MCP-Approval-Requests zusaetzlich als RAGAI-Overlay angezeigt werden.
   * In der Chat-Ansicht wird die Anfrage bereits inline als Nachricht dargestellt,
   * daher bleibt das Overlay dort deaktiviert. Avatar-/Audio-Ansichten aktivieren es. */
  protected showMcpApprovalOverlayEnabled = false;
  private markdownService = inject(MarkdownService);
  protected usertokenService = inject(UsertokenService);

  constructor(
    public route: ActivatedRoute,
    public loginService: LoginService,
    public speechService: SpeechService,
    public statisticsService: StatisticService,
    public iframeCommunication: IframeCommunicationService,
    public playService: PlayAudioService,
    public bot: BotService,
    public socketService: SocketService,
    public toastr: ToastrService,
    public modalService: BsModalService,

    public domSanatizer: DomSanitizer,
    protected botMessages: BotMessagesService,
    public logger: RagLoggerService,
    protected overlayManager: OverlayManagerService
  ) {
    route.data.subscribe(data => {
      const botConfig = data['botConfig'] as ChatbotConfig | null;
      if (!botConfig) return;
      this.botConfig = botConfig;
    });

    this.route.queryParams.subscribe(params => {
      this.recognitionLanguage = "" + (params['recognitionlanguage'] ?? 'de-DE');
    });

    this.subscriptions.push(
      this.socketService.onCreateMessagesInThread.subscribe(data => {

        for (const message of data.messages) {
          if (!message.bypass) {
            //this.isThinking = true;
            this.speechService.botIsThinkking = true;
          }
          this.botMessages.addMessage(message);
        }
      }),
      this.socketService.onRunComplete.subscribe(data => {
        if (data.role === "mcp_approval_request") {
          this.botMessages.addMessage(data);
          if (this.showMcpApprovalOverlayEnabled) {
            this.showMcpApprovalOverlay(data);
          }
        }
      }),
      this.bot.resetBotDoneEmitter.subscribe(() => {
        this.botMessages.setMessages([]);
      }),
      this.socketService.onAddUserMessage.subscribe(data => {
        //this.isThinking = true;
        this.speechService.botIsThinkking = true;
        this.botMessages.addMessage(data.message);
      }),
      this.socketService.onRunUpdate.subscribe(this.onRunUpdate.bind(this)),
      this.socketService.onStateUpdate.subscribe(this.onStateUpdate.bind(this)),
      this.socketService.onRunFailed.subscribe(error => {
        this.botState = "idle";
        this.toastr.error('ERROR', error, { timeOut: 30000 });
      }),
      this.socketService.onRunIncomplete.subscribe(error => {
        this.botState = "idle";
        this.toastr.error('ERROR', error, { timeOut: 30000 });
      }),
      this.socketService.onSupervisorMessage.subscribe((supervisorMessage) => {
        const options: ModalOptions = {
          class: 'modal-lg',
          initialState: {
            supervisorMessage: supervisorMessage.message,
            botId: this.botConfig._id
          },
          keyboard: false,
          ignoreBackdropClick: true
        };
        this.supervisorAnswerModalRef = this.modalService.show(SupervisorMessageModalComponent, options);

      }),
      this.socketService.onLogoutOldSession.subscribe((data) => {
        if (this.loginService.loggedInUser && this.loginService.loggedInUser._id === data.oldSessionId) {
          void this.loginService.logout(false);
        }
      })
    );
  }

  public async ngOnInit() {
    if (this.loginService.loggedInUser === null) {
      // persist=false: Token wird erst nach Bestaetigung des Datenschutzhinweises persistiert
      await this.loginService.loadMyUser(false);
    }

    // CustomJS vor dem Disclaimer laden, damit initialIdle() die Subscription aufbauen
    // kann bevor der Socket nach Disclaimer-Annahme sofort "idle" sendet.
    this.initIFrameService();
    if (this.botConfig as ChatbotConfig | null) {
      this.injectJs(this.botConfig._id);
    }

    await this.showGDPR();
    // Erst nach Anzeige/Bestaetigung des Disclaimers darf der Usertoken dauerhaft im LocalStorage landen
    this.usertokenService.persistToken();

    this.botMessages.setMessages((await this.bot.getHistoryMessages(this.botConfig._id)).messageHistory);

    this.speechService.textShouldBeSend.subscribe((send: boolean) => {
      if (send) {
        void this.add();
        this.promptInput = '';
        this.playService.stopAudio();
      }
    });

    this.playService.currentAudioStatus.subscribe((status: [string, string]) => {
      if (status[0] === 'idle') {
        this.promptInput = '';
      }
    });

    this.speechService.recognisedTextChanged.subscribe((text: string) => {
      if (this.isThinking || this.playService.anyAudioIsLoading() || this.playService.anyAudioIsPlaying()) return;
      this.promptInput = text;
    });

    if (this.botConfig as ChatbotConfig | null) {
      if (this.botConfig.voiceInputChatActive || this.botConfig.voiceOutputChatActive) {
        this.speechService.init(this.recognitionLanguage);
      }
      this.socketService.connectToThread(this.botConfig._id);
    }
  }

  public ngOnDestroy() {
    this.subscriptions.forEach(sub => { sub.unsubscribe(); });
  }

  public onRunUpdate(data: Omit<HistoryItemResponse, 'content'> & { snapshot: string; deltaStr: string; }) {
    this.isRecivingDeltas = true;
    this.isThinking = true;
    this.speechService.botIsThinkking = true;
    let lastIndex = this.botMessages.getMessageCount() - 1;
    const current = this.botMessages.getMessage(lastIndex);
    if (current) {
      if (current.item_id != data.item_id) {
        this.botMessages.addRunMessage(data);
        const newCurrent = this.botMessages.getMessage(lastIndex + 1);
        if (newCurrent) {
          lastIndex += 1;
        }
      }
      this.botMessages.updateContent(lastIndex, data.snapshot);
    } else {
      this.botMessages.addRunMessage(data);
    }
  }

  /** der socket hat nen neuen state */
  public async onStateUpdate(data: { sessionState: SessionState }) {
    //    this.logger.measureRunStep("recive " + data.sessionState, "in");
    if (data.sessionState == 'idle') {
      //      this.logger.measureRunStop();
      this.isRecivingDeltas = false;
      this.isThinking = false;
    }

    this.botState = data.sessionState;

    this.updateBrowserTitle();
    if (this.botState == "openaierror") {
      this.onOpenAIError();
    }

    if (this.botState === "idle") {
      const isFirst = this.prevBotState == "unknown" || this.prevBotState == "init_assistant";
      if (isFirst) { // hier mal scaun
        await this.onInitialIdle();
      }
      await this.onMessagReceived(isFirst);
    }
    this.prevBotState = data.sessionState;
  }

  public async onInitialIdle() {
    const initialMessages = await this.iframeCommunication.RAGAI.initialMessages();
    if (initialMessages.length > 0) {
      await this.iframeCommunication.RAGAI.addMessagesToThread(initialMessages);
      //      RAGAI.EVENTS.trigger("RAGAI.BOT.STARTED_WITH_HISTIo")

    }

    if (this.botMessages.findLatestAssistantMessage() == null) {
      const mySubscription = this.socketService.onStateUpdate.subscribe((event) => {
        if (event.sessionState === "idle") {
          this.bot.botEvents.emit({ type: BOTEVENT.WelcomeAndHistoryFinished, data: {} });
          mySubscription.unsubscribe();
        }
      });
    } else {
      this.bot.botEvents.emit({ type: BOTEVENT.WelcomeAndHistoryFinished, data: {} });
    }
  }

  public async onMessagReceived(isFirst: boolean) {
    if (!isFirst) {
      const lastIndex = this.botMessages.getMessageCount() - 1;
      const current = this.botMessages.getMessage(lastIndex);
      if (current && current._id != this.lastRunCompleteMessageId) {
        this.lastRunCompleteMessageId = current._id;
        await this.broadCastMSG(current);
        this.iframeCommunication.runComplete(current);
      }
    }
  }

  public onOpenAIError() {
    this.toastr.error("SENT", "Bitte versuche es erneut.");
    // nachdem der toaster flog, soll debot wieder idle sein dammit der user es neu probieren kann
    this.botState = "idle";
  }

  public updateBrowserTitle() {
    window.document.title = "(" + this.botState + ") " + this.botConfig.name;
  }

  public async removePassThrough() {
    if (!(await this.bot.hasBotAccessByAdmin(this.botConfig._id))) {
      localStorage.removeItem("passthrough");
    }
  }

  public initIFrameService() {

    this.iframeCommunication.msgManager.runRequestEmitter.subscribe((prompt: string) => {
      void this.add(prompt, true);
    });

    this.iframeCommunication.msgManager.userMessageEmitter.subscribe((message: string) => {
      this.promptInput = message;
      void this.add();
    });
  }

  protected injectJs(botId: string) {
    const url = window.location.origin + "/api/general/js/" + botId + "/custom.js";
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    document.head.appendChild(script);
  }


  protected injectCSS(botId: string, type: "chat" | "voice" | "avatar" | "did-avatar") {
    const url = "/api/general/css/" + botId + "/" + type + "/custom.css";
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);

  }

  //nonPomtInput wenn über BotApi Prompt gesendet wird dann das Input feld in ruhe lassen
  public async add(nonPromtInput: string | null = null, bypass: boolean = false) {
    //    this.logger.callerInfo(this);
    let pInput = nonPromtInput ?? this.promptInput;
    if (pInput === '') return;
    //? Check for stop words
    const foundStopWords = this.promptContainsStopWord(pInput, this.botConfig.stopWords);
    if (foundStopWords.length > 0) {
      this.toastr.error("SENT", "Unerwünschte Wörter gefunden: " + foundStopWords.join(", "));
      return;
    }
    //? Check for word replacements
    pInput = this.runWordReplacer(pInput);
    this.isThinking = true;
    this.speechService.botIsThinkking = true;
    if (nonPromtInput == null) {
      this.promptInput = "";
    }
    window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.MSG.prompt", { pInput });
    const res = await this.bot.chat(this.botConfig._id, pInput, this.botMessages.getMessages(), bypass);
    if (res) {
      window.RAGAI.lastThreadId = res.threadId;
      this.currentThread = res.threadId;
    }
  }

  protected promptContainsStopWord(prompt: string, stopWords: string[]): string[] {
    const splittedPrompt = prompt.split(new RegExp("[^a-zA-Z0-9äöüÄÖÜß]+"));
    return stopWords.filter((word: string) => {
      return splittedPrompt.some((stopWord: string) => word.toLowerCase().trim() == stopWord.toLowerCase().trim());
    });
  }

  protected runWordReplacer(str: string) {
    this.botConfig.wordReplacements.forEach((wr: WordReplacement) => {
      str = str.replace(new RegExp("\\b" + wr.word + "\\b", 'gi'), wr.replacement);
    });
    return str;
  }

  public async cancelThinking() {
    await this.bot.sendBotThinkingCancel(this.botConfig._id, this.currentThread);
  }

  protected async broadCastMSG(msg: HistoryItemResponse) {
    // MCP-Rollen (mcp_call, mcp_approval_request, mcp_approval_response) sollen nie vorgelesen werden
    if (this.botConfig.voiceAutoPlay && msg.role === 'assistant') {
      if (this.speechService.isInConversationMode) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const message = msg.content.at(0)!;
        let m: string = '';
        if (message.type === 'text') {
          m = message.text;
        }
        await this.playService.playAudio(m, this.botConfig._id, this.botConfig.voiceConfig.id);
      }
    }
    parent.postMessage(msg, '*');
  }

  // Bot-App wird pro Sprache als eigenes Bundle gebaut (siehe bot/angular.json i18n.locales);
  // Angular CLI setzt <html lang> je Build-Locale automatisch.
  private getLocalizedComplianceText(): string {
    const locale = document.documentElement.lang.toLowerCase() as BotLocale;
    const complianceText = this.botConfig.complianceText;
    // Fallback für ältere/nicht migrierte Bots, bei denen das Feld noch ein einzelner String ist
    if (complianceText == null || typeof complianceText === 'string') {
      return locale === 'de' ? ((complianceText as unknown as string) ?? '') : '';
    }
    return complianceText[locale] ?? '';
  }

  public showGDPR(): Promise<void> {
    const complianceText = this.getLocalizedComplianceText();
    if (complianceText !== '' && localStorage.getItem('disclaimerDontShowAgain_' + this.botConfig._id) !== 'true') {
      // Access to window.parent properties is blocked by Same-Origin Policy in cross-origin iframes.
      let fusionmeta: { disclaimerPlacement?: string } | undefined;
      try {
        fusionmeta = (window.parent as Window & { fusionmeta?: { disclaimerPlacement?: string } }).fusionmeta;
      } catch (e) {
        // Cross-origin parent: fall through to local modal
        console.error(e);
      }
      if (fusionmeta?.disclaimerPlacement === 'fusionview') {
        return this.showGDPRInParent(complianceText);
      }
      return new Promise<void>(resolve => {
        document.body.classList.add('disclaimer-open');
        const modalRef = this.modalService.show(DisclaimerModalComponent, {
          class: 'modal-lg disclaimer-modal',
          keyboard: false,
          ignoreBackdropClick: true,
          initialState: {
            text: complianceText
          }
        });
        modalRef.onHide?.subscribe(() => {
          document.body.classList.remove('disclaimer-open');
          const dontShowAgain = modalRef.content?.dontShowAgain;
          if (dontShowAgain) {
            localStorage.setItem('disclaimerDontShowAgain_' + this.botConfig._id, 'true');
          }
          resolve();
        });
      });
    }
    return Promise.resolve();
  }

  private async showGDPRInParent(complianceText: string): Promise<void> {
    const parsedHtml = await Promise.resolve(this.markdownService.parse(complianceText));
    const botId = this.botConfig._id;
    let resolveConfirmed!: () => void;
    const confirmed = new Promise<void>(r => { resolveConfirmed = r; });

    const overlay = parent.document.createElement('div');
    overlay.id = 'ragai-disclaimer-overlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,1);z-index:2147483647;display:flex;align-items:center;justify-content:center;font-family:system-ui,sans-serif;';

    const modal = parent.document.createElement('div');
    modal.style.cssText = 'background:#fff;border-radius:8px;padding:24px;max-width:600px;width:90%;max-height:90svh;overflow-y:auto;box-shadow:0 10px 40px rgba(0,0,0,0.3);';

    const header = parent.document.createElement('div');
    header.style.cssText = 'margin-bottom:16px;';
    const title = parent.document.createElement('h4');
    title.style.cssText = 'margin:0;font-size:1.1rem;font-weight:600;';
    title.textContent = $localize`:@@disclaimer-title:Disclaimer`;
    header.appendChild(title);

    const content = parent.document.createElement('div');
    content.style.cssText = 'font-size:0.9rem;line-height:1.5;';
    content.innerHTML = typeof parsedHtml === 'string' ? parsedHtml : '';

    const hr = parent.document.createElement('hr');
    hr.style.cssText = 'margin:16px 0;';

    const footer = parent.document.createElement('div');
    footer.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:12px;flex-shrink:0;';

    const checkboxLabel = parent.document.createElement('label');
    checkboxLabel.style.cssText = 'display:flex;align-items:center;gap:6px;cursor:pointer;font-size:0.9rem;';
    const checkbox = parent.document.createElement('input');
    checkbox.type = 'checkbox';
    checkboxLabel.appendChild(checkbox);
    checkboxLabel.appendChild(parent.document.createTextNode($localize`:@@disclaimer-dont-show-again:Nicht mehr anzeigen`));

    const btn = parent.document.createElement('button');
    btn.textContent = $localize`:@@confirm:Verstanden`;
    btn.style.cssText = 'padding:8px 20px;background:#0d6efd;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:0.9rem;white-space:nowrap;';
    btn.onclick = () => {
      if (checkbox.checked) {
        localStorage.setItem('disclaimerDontShowAgain_' + botId, 'true');
      }
      overlay.remove();
      resolveConfirmed();
    };

    footer.appendChild(checkboxLabel);
    footer.appendChild(btn);
    modal.appendChild(header);
    modal.appendChild(content);
    modal.appendChild(hr);
    modal.appendChild(footer);
    overlay.appendChild(modal);
    parent.document.body.appendChild(overlay);
    await confirmed;
  }

  protected getCustomCssUrl(botId: string, type: "chat" | "voice" | "avatar" | "did-avatar") {
    const url = "/api/general/css/" + botId + "/" + type + "/custom.css";
    return this.domSanatizer.bypassSecurityTrustResourceUrl(url);
  }

  protected openCardAsModal(content: HistoryItemResponse["content"]) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const msg = content.at(0)!;
    let m: string = '';
    if (msg.type === 'text') {
      m = msg.text;
    }
    this.modalService.show(CardModalComponent, { initialState: { content: JSON.parse(m) }, class: 'modal-card' });
  }

  /** Zeigt einen ausstehenden MCP-Approval-Request als RAGAI-Overlay an – fuer Ansichten
   * (Avatar, Live-Avatar, D-Id-Avatar, Audio-Bot), die keine Chat-Nachrichtenliste rendern. */
  protected showMcpApprovalOverlay(item: HistoryItemResponse): void {
    const mcpApprovalRequest = item.content.at(0);
    if (mcpApprovalRequest?.type !== "mcp_approval_request") return;

    let argsPreview = mcpApprovalRequest.arguments;
    try {
      argsPreview = JSON.stringify(JSON.parse(mcpApprovalRequest.arguments), null, 2);
    } catch {
      // Argumente sind kein valides JSON, dann Rohtext anzeigen
    }

    const toolName = mcpApprovalRequest.name.charAt(0).toUpperCase() + mcpApprovalRequest.name.slice(1);
    // Wichtig: kein Zeilenumbruch innerhalb des Template-Literals, da OverlayManagerService \n durch <br> ersetzt
    // und das sonst die Flex-Zeile mit Tool-/Server-Namen auseinanderreisst (nicht mehr mittig ausgerichtet).
    // Der style-Tag ueberschreibt den halbtransparenten Standard-Overlay-Hintergrund mit einer deckenden Farbe.
    const text = `<style>#custom-overlay{background:#14151a !important;}</style>`
      + `<div style="background:#20232a;color:#fff;border-radius:10px;padding:20px 24px;">`
      + `<div style="display:flex;align-items:center;justify-content:center;gap:8px;margin-bottom:10px;">`
      + `<i class="pi pi-bolt"></i>`
      + `<span>Tool <strong>${toolName}</strong></span>`
      + `<span style="font-size:0.7em;background:rgba(255,255,255,0.15);border-radius:4px;padding:2px 8px;">${mcpApprovalRequest.server_label}</span>`
      + `</div>`
      + `<pre style="margin:0;padding:10px 12px;font-size:0.65em;border-radius:6px;background:rgba(0,0,0,0.35);max-height:200px;overflow:auto;white-space:pre-wrap;word-break:break-word;text-align:left;">${argsPreview}</pre>`
      + `</div>`;

    let alreadyAnswered = false;
    const respond = async (approve: boolean) => {
      if (alreadyAnswered) return;
      alreadyAnswered = true;
      this.overlayManager.close();
      const response: MessageItem = {
        role: "mcp_approval_response",
        content: [{
          type: "mcp_approval_response",
          approve,
          approval_request_id: mcpApprovalRequest.id
        }]
      };
      await this.bot.mcpApprovalResponse(this.botConfig._id, response, this.botMessages.getMessages());
    };

    this.overlayManager.textAndButton(
      text,
      [
        { text: "Zulassen", onClick: () => { void respond(true); }, className: "btn btn-primary" },
        { text: "Überspringen", onClick: () => { void respond(false); }, className: "btn btn-outline-light" }
      ],
      () => { void respond(false); }
    );
  }

}
